import { apiClient } from './client';
import type { BalanceGeneralRow, ConfigEstadoResultado, CostoSubaplicado, CuentaContable, CuentaSaldo, DashboardRow, EmbarqueDetalle, MovimientosCuentaResponse, Periodo } from '@/shared/types';

export interface DashboardParams {
  compania: string;
  periodo: string;
  cantidad_meses?: number;
  incluir_abierto?: boolean;
}

export async function fetchDashboard(params: DashboardParams): Promise<DashboardRow[]> {
  const { data } = await apiClient.post<DashboardRow[]>('/mobile/ef/dashboard', params);
  return data;
}

export interface DashboardAnualParams {
  compania: string;
  anio_inicio: number;
  anio_fin: number;
  incluir_abierto?: boolean;
}

export async function fetchDashboardAnual(
  params: DashboardAnualParams
): Promise<DashboardRow[]> {
  const { data } = await apiClient.post<DashboardRow[]>('/mobile/ef/dashboard-anual', params);
  return data;
}

export interface PeriodoCerrado {
  value: string;
  textContent: string;
  estado?: 'CERRADO' | 'REVISION';
}

export async function fetchPeriodosCerrados(
  compania: string,
  incluirAbierto?: boolean
): Promise<PeriodoCerrado[]> {
  const { data } = await apiClient.post<PeriodoCerrado[]>('/mobile/ef/periodos-cerrados', {
    compania,
    incluir_abierto: incluirAbierto ?? false,
  });
  return data;
}

export interface CentroCosto {
  value: string;
  textContent: string;
}

export async function fetchCentrosCosto(compania: string): Promise<CentroCosto[]> {
  const { data } = await apiClient.post<CentroCosto[]>('/mobile/ef/centros-costo', {
    compania,
  });
  return data;
}

export interface GastoCentroCosto {
  CENTRO_COSTO: string;
  NOMBRE_CENTRO_COSTO: string;
  CUENTA_MADRE: string;
  DESCRIPCION_MADRE: string;
  CUENTA_CONTABLE: string;
  DESCRIPCION: string;
  GASTOS: number;
  PRESUPUESTO: number;
  DEDUCCION_ALIMENTOSCOOK: number;
  GASTO_NETO: number;
  DIFERENCIA: number;
  VARIACION_PRESUPUESTO: number | null;
}

export interface GastosCentroCostoParams {
  compania: string;
  annio: number;
  mes: number;
  centro_costo?: string;
  incluir_abierto?: boolean;
}

export async function fetchGastosCentroCosto(
  params: GastosCentroCostoParams
): Promise<GastoCentroCosto[]> {
  const { data } = await apiClient.post<GastoCentroCosto[]>('/mobile/ef/gastos-centro-costo', params);
  return data;
}

export interface GastoCentroCostoHistorico {
  ANNIO: number;
  MES: number;
  CENTRO_COSTO: string;
  NOMBRE_CENTRO_COSTO: string;
  CUENTA_MADRE: string;
  DESCRIPCION_MADRE: string;
  CUENTA_CONTABLE: string;
  DESCRIPCION: string;
  GASTOS: number;
  PRESUPUESTO: number;
  DEDUCCION_ALIMENTOSCOOK: number;
  GASTO_NETO: number;
  DIFERENCIA: number;
  VARIACION_PRESUPUESTO: number | null;
}

export interface GastosCentroCostoHistoricoParams {
  compania: string;
  periodo: string;
  centro_costo?: string;
  cantidad_meses?: number;
  incluir_abierto?: boolean;
}

export async function fetchGastosCentroCostoHistorico(
  params: GastosCentroCostoHistoricoParams
): Promise<GastoCentroCostoHistorico[]> {
  const { data } = await apiClient.post<GastoCentroCostoHistorico[]>(
    '/mobile/ef/gastos-centro-costo-historico',
    params
  );
  return data;
}

export async function fetchPeriodos(): Promise<Periodo[]> {
  const { data } = await apiClient.post<Periodo[]>('/mobile/ef/periodos', {
    action: 'GET_HEADERS',
  });
  return data;
}

export interface PeriodoActionParams {
  action:
    | 'OPEN_PERIOD'
    | 'REGENERATE_PERIOD'
    | 'CLOSE_PERIOD'
    | 'REOPEN_PERIOD'
    | 'DELETE_PERIOD'
    | 'GET_PERIOD_DETAIL'
    | 'GET_PERIOD_FACT_DETAIL';
  id?: number;
  fecha_periodo?: string;
  compania?: string;
  empleado?: string;
}

export async function executePeriodoAction(
  params: PeriodoActionParams
): Promise<{ STATUS: string }> {
  const { data } = await apiClient.post<{ STATUS: string }>('/mobile/ef/periodos', params);
  return data;
}

export interface PeriodoDetalleGasto {
  gastos: number;
  deduccionAlimentoscook: number;
  gastoNeto: number;
  clasificacion: string;
  centroCosto: string;
  cuentaContable: string;
  descripcion: string;
  cuentaMadre: string;
  descripcionMadre: string;
  annio: number;
  mes: number;
}

export interface PeriodoDetalleFacturacion {
  monto: number;
  centroCosto: string;
  clasificacion: string;
  cuentaContable: string;
  descripcion: string;
  annio: number;
  mes: number;
}

export async function fetchPeriodoDetalleGastos(id: number): Promise<PeriodoDetalleGasto[]> {
  const { data } = await apiClient.post<PeriodoDetalleGasto[]>('/mobile/ef/periodos', {
    action: 'GET_PERIOD_DETAIL',
    id,
  });
  return data;
}

export async function fetchPeriodoDetalleFacturacion(
  id: number
): Promise<PeriodoDetalleFacturacion[]> {
  const { data } = await apiClient.post<PeriodoDetalleFacturacion[]>('/mobile/ef/periodos', {
    action: 'GET_PERIOD_FACT_DETAIL',
    id,
  });
  return data;
}

export interface DeduccionCook {
  ID: number;
  CLASIFICACION: string;
  MONTO: number;
  MONTO_FMT: string;
  FECHA: string;
  CREADO_POR: string;
  FECHA_CREACION: string;
}

export interface DeduccionCatalogo {
  value: string;
  textContent: string;
}

export async function fetchDeducciones(): Promise<DeduccionCook[]> {
  const { data } = await apiClient.post<DeduccionCook[]>('/mobile/ef/deducciones', {
    action: 'GET_DEDUCCIONES',
  });
  return data;
}

export async function fetchDeduccionesClasificaciones(): Promise<DeduccionCatalogo[]> {
  const { data } = await apiClient.post<DeduccionCatalogo[]>('/mobile/ef/deducciones', {
    action: 'GET_CLASIFICACIONES',
  });
  return data;
}

export interface DeduccionActionParams {
  action: 'INSERT' | 'DELETE';
  periodo?: string;
  id?: number;
  monto?: number;
  clasificacion?: string;
  empleado?: string;
}

export async function executeDeduccionAction(
  params: DeduccionActionParams
): Promise<{ STATUS: string }> {
  const { data } = await apiClient.post<{ STATUS: string }>('/mobile/ef/deducciones', params);
  return data;
}

export interface ClasificacionEF {
  ID: number;
  DESCRIPCION: string;
  COMPANIA: string;
  /** Lista separada por comas; null cuando la clasificaci�n es nueva y no tiene centros. */
  CENTROS_COSTO: string | null;
}

export async function fetchClasificaciones(): Promise<ClasificacionEF[]> {
  const { data } = await apiClient.post<ClasificacionEF[]>('/mobile/ef/clasificaciones', {
    action: 'GET_CLASIFICACIONES',
  });
  return data;
}

export async function createClasificacion(
  descripcion: string,
  compania: string
): Promise<ClasificacionEF> {
  const { data } = await apiClient.post<ClasificacionEF>('/mobile/ef/clasificaciones', {
    action: 'CREATE_CLASIFICACION',
    descripcion,
    compania,
  });
  return data;
}

export async function updateClasificaciones(
  data: Array<{ ID: number; COMPANIA: string; CENTROS_COSTO: string[] }>
): Promise<{ STATUS: string }> {
  const { data: response } = await apiClient.post<{ STATUS: string }>('/mobile/ef/clasificaciones', {
    action: 'UPDATE_CLASIFICACIONES',
    data,
  });
  return response;
}

export interface BalanceGeneralParams {
  compania: string;
  periodo: string;
  cantidad_meses?: number;
  modo?: 'resumido' | 'detallado';
}

export async function fetchBalanceGeneral(
  params: BalanceGeneralParams
): Promise<BalanceGeneralRow[]> {
  const { data } = await apiClient.post<BalanceGeneralRow[]>('/mobile/ef/balance-general', params);
  return data;
}

export interface ConfigEstadoResultadoParams {
  compania?: string;
  concepto?: string;
}

export interface ConfigEstadoResultadoPayload {
  compania: string;
  concepto: string;
  centro_costo?: string | null;
  cuenta_contable?: string | null;
  incluir_en_total: boolean;
  orden?: number | null;
}

export async function fetchConfigEstadoResultado(
  params?: ConfigEstadoResultadoParams
): Promise<ConfigEstadoResultado[]> {
  const { data } = await apiClient.get<ConfigEstadoResultado[]>('/mobile/ef/config-estado-resultado', {
    params,
  });
  return data;
}

export async function createConfigEstadoResultado(
  payload: ConfigEstadoResultadoPayload
): Promise<ConfigEstadoResultado> {
  const { data } = await apiClient.post<ConfigEstadoResultado>('/mobile/ef/config-estado-resultado', payload);
  return data;
}

export async function updateConfigEstadoResultado(
  id: number,
  payload: ConfigEstadoResultadoPayload
): Promise<ConfigEstadoResultado> {
  const { data } = await apiClient.put<ConfigEstadoResultado>(`/mobile/ef/config-estado-resultado/${id}`, payload);
  return data;
}

export async function deleteConfigEstadoResultado(id: number): Promise<{ message: string }> {
  const { data } = await apiClient.delete<{ message: string }>(`/mobile/ef/config-estado-resultado/${id}`);
  return data;
}

export interface CostosSubaplicadosParams {
  compania?: string;
  anio?: number;
}

export interface CostoSubaplicadoPayload {
  cia: string;
  anio: number;
  mes: number;
  costo_fletes_exportaciones: number;
  costo_tarimas_embalaje: number;
  otros_insumos_menores: number;
  variacion: number;
}

export type CostoSubaplicadoUpdatePayload = Omit<CostoSubaplicadoPayload, 'cia' | 'anio' | 'mes'>;

export async function fetchCostosSubaplicados(
  params?: CostosSubaplicadosParams
): Promise<CostoSubaplicado[]> {
  const { data } = await apiClient.get<CostoSubaplicado[]>('/mobile/ef/costos-subaplicados', {
    params,
  });
  // sqlsrv devuelve los decimal como string; se normalizan a number.
  return data.map(row => ({
    ...row,
    ANIO: Number(row.ANIO),
    MES: Number(row.MES),
    COSTO_FLETES_EXPORTACIONES: Number(row.COSTO_FLETES_EXPORTACIONES),
    COSTO_TARIMAS_EMBALAJE: Number(row.COSTO_TARIMAS_EMBALAJE),
    OTROS_INSUMOS_MENORES: Number(row.OTROS_INSUMOS_MENORES),
    VARIACION: Number(row.VARIACION),
  }));
}

export async function createCostoSubaplicado(
  payload: CostoSubaplicadoPayload
): Promise<CostoSubaplicado> {
  const { data } = await apiClient.post<CostoSubaplicado>('/mobile/ef/costos-subaplicados', payload);
  return data;
}

export async function updateCostoSubaplicado(
  cia: string,
  anio: number,
  mes: number,
  payload: CostoSubaplicadoUpdatePayload
): Promise<CostoSubaplicado> {
  const { data } = await apiClient.put<CostoSubaplicado>(
    `/mobile/ef/costos-subaplicados/${cia}/${anio}/${mes}`,
    payload
  );
  return data;
}

// =============================================================================
// AGRUPACIONES DE AN�LISIS (no van al P&L)
// =============================================================================

export interface AgrupacionAnalisis {
  ID: number;
  DESCRIPCION: string;
  COMPANIA: string;
  /** Lista separada por comas; puede venir vac�a. */
  CENTROS_COSTO: string;
}

export interface AgrupacionAnalisisCreatePayload {
  descripcion: string;
  compania: string;
  centros_costo: string[];
}

export interface AgrupacionAnalisisUpdatePayload {
  descripcion: string;
  centros_costo: string[];
}

export async function fetchAgrupacionesAnalisis(
  compania?: string
): Promise<AgrupacionAnalisis[]> {
  const { data } = await apiClient.post<AgrupacionAnalisis[]>('/mobile/ef/agrupaciones-analisis', {
    action: 'GET_AGRUPACIONES',
    ...(compania ? { compania } : {}),
  });
  return data;
}

export async function createAgrupacionAnalisis(
  payload: AgrupacionAnalisisCreatePayload
): Promise<AgrupacionAnalisis> {
  const { data } = await apiClient.post<AgrupacionAnalisis>('/mobile/ef/agrupaciones-analisis', {
    action: 'CREATE_AGRUPACION',
    ...payload,
  });
  return data;
}

export async function updateAgrupacionAnalisis(
  id: number,
  payload: AgrupacionAnalisisUpdatePayload
): Promise<AgrupacionAnalisis> {
  const { data } = await apiClient.post<AgrupacionAnalisis>('/mobile/ef/agrupaciones-analisis', {
    action: 'UPDATE_AGRUPACION',
    id,
    ...payload,
  });
  return data;
}

export async function deleteAgrupacionAnalisis(id: number): Promise<{ message: string }> {
  const { data } = await apiClient.post<{ message: string }>('/mobile/ef/agrupaciones-analisis', {
    action: 'DELETE_AGRUPACION',
    id,
  });
  return data;
}

// =============================================================================
// MAYOR POR CUENTA (consulta en tiempo real de EXACTUS)
// =============================================================================

export interface MayorSaldosParams {
  compania: string;
  anio: number;
  mes: number;
}

export interface MayorMovimientosParams extends MayorSaldosParams {
  cuenta: string;
  page?: number;
  per_page?: number;
  referencia?: string;
  asiento?: string;
  centro_costo?: string;
  fecha?: string;
}

export async function fetchMayorCuentas(compania: string): Promise<CuentaContable[]> {
  const { data } = await apiClient.get<CuentaContable[]>('/mobile/ef/mayor/cuentas', {
    params: { compania },
  });
  return data;
}

export async function fetchMayorSaldos(params: MayorSaldosParams): Promise<CuentaSaldo[]> {
  const { data } = await apiClient.get<CuentaSaldo[]>('/mobile/ef/mayor/saldos', { params });
  // sqlsrv devuelve los decimal como string; se normalizan a number.
  return data.map(row => ({
    ...row,
    SALDO: Number(row.SALDO),
    DEBITO_MES: Number(row.DEBITO_MES),
    CREDITO_MES: Number(row.CREDITO_MES),
  }));
}

export async function fetchMayorMovimientos(
  params: MayorMovimientosParams
): Promise<MovimientosCuentaResponse> {
  const { data } = await apiClient.get<MovimientosCuentaResponse>(
    '/mobile/ef/mayor/movimientos',
    { params }
  );
  const toNum = (v: number | null): number | null => (v === null ? null : Number(v));
  // sqlsrv puede devolver FECHA como string o como objeto DateTime de PHP ({ date, ... })
  const toFecha = (v: unknown): string => {
    if (typeof v === 'string') return v.slice(0, 10);
    if (v && typeof v === 'object' && 'date' in v) {
      return String((v as { date: string }).date).slice(0, 10);
    }
    return String(v ?? '');
  };
  return {
    mayor: {
      ...data.mayor,
      total: Number(data.mayor.total),
      total_debito: Number(data.mayor.total_debito),
      total_credito: Number(data.mayor.total_credito),
      data: data.mayor.data.map(row => ({
        ...row,
        FECHA: toFecha(row.FECHA),
        DEBITO_LOCAL: toNum(row.DEBITO_LOCAL),
        CREDITO_LOCAL: toNum(row.CREDITO_LOCAL),
      })),
    },
    pendientes: data.pendientes.map(row => ({
      ...row,
      FECHA: toFecha(row.FECHA),
      DEBITO_LOCAL: toNum(row.DEBITO_LOCAL),
      CREDITO_LOCAL: toNum(row.CREDITO_LOCAL),
    })),
  };
}

export async function fetchEmbarque(compania: string, embarque: string): Promise<EmbarqueDetalle> {
  const { data } = await apiClient.get<EmbarqueDetalle>('/mobile/ef/mayor/embarque', {
    params: { compania, embarque },
  });
  return {
    encabezado: data.encabezado,
    lineas: data.lineas.map(l => ({
      ...l,
      CANTIDAD_EMBARCADA: Number(l.CANTIDAD_EMBARCADA),
      CANTIDAD_RECIBIDA: Number(l.CANTIDAD_RECIBIDA),
      COST_UN_FISC_LOCAL: Number(l.COST_UN_FISC_LOCAL),
    })),
  };
}
